import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import type { FunctionComponent } from "react";
import { Fragment } from "react";
import { NavLink } from "react-router-dom";

import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import type { OrderWithBillingSettings } from "~/modules/project/api/order/orderTypes.ts";
import type { ProjectPhase } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import ProjectRevenueInvoiceEntry
	from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/components/ProjectRevenueInvoiceEntry/ProjectRevenueInvoiceEntry.tsx";
import type {
	ProjectRevenueTimelineRow,
} from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/projectRevenueReportUtils.ts";
import { formatMonthLabel } from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/projectRevenueReportUtils.ts";
import { getPhaseOrderPath, getPhasePath } from "~/modules/project/utils/projectDetailsViewUtils.ts";
import { getFullPhaseTitle } from "~/modules/project/utils/projectPhaseUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	row: ProjectRevenueTimelineRow;
	projectId: string;
	phasesById: Record<string, ProjectPhase>;
	ordersById: Record<string, OrderWithBillingSettings>;
	invoicesById: Record<string, Invoice>;
	isExpanded: boolean;
	onToggle: (month: string) => void;
};

const ProjectRevenueMonthRow: FunctionComponent<Props> = ({
	row,
	projectId,
	phasesById,
	ordersById,
	invoicesById,
	isExpanded,
	onToggle,
}) => {
	const { month, monthData, invoices } = row;
	const isExpandable = !!monthData && monthData.phasesBreakdown.length > 0;

	const sortedPhases = monthData
		? [...monthData.phasesBreakdown].sort(
			(a, b) => (phasesById[b.phaseId]?.phaseNumber ?? 0) - (phasesById[a.phaseId]?.phaseNumber ?? 0),
		)
		: [];

	return <tbody className="group/month">
		<tr className="border-b border-gray-100 text-sm transition-colors hover:bg-gray-200 group-hover/month:bg-gray-200">
			<td className="px-2 py-3">
				<button type="button"
						disabled={!isExpandable}
						onClick={() => onToggle(month)}
						className="flex items-center gap-x-2 disabled:cursor-default">
					{isExpandable
						? (isExpanded ? <ChevronDownIcon className="size-4" /> : <ChevronRightIcon className="size-4" />)
						: <span className="size-4" />}
					<span className="font-medium capitalize text-gray-900">{formatMonthLabel(month)}</span>
				</button>
			</td>
			<td className="px-4 py-3 text-right font-semibold text-gray-900">
				{formatCentsToCurrency(monthData?.totalTrackedBillableCents ?? 0)}
			</td>
			<td className="px-4 py-3 text-right text-gray-400">
				{formatCentsToCurrency(monthData?.totalTrackedNotBillableCents ?? 0)}
			</td>
			<td className="px-4 py-3">
				<div className="flex flex-col gap-y-2">
					{invoices.map((invoice) => <ProjectRevenueInvoiceEntry key={invoice.invoiceId}
																		   invoice={invoice}
																		   invoiceDetails={invoicesById[invoice.invoiceId]} />)}
				</div>
			</td>
		</tr>

		{isExpanded && sortedPhases.map((phaseBreakdown) => {
			const phase = phasesById[phaseBreakdown.phaseId];
			const phaseTitle = phase ? getFullPhaseTitle(phase) : "Unbekannte Phase";

			return <Fragment key={phaseBreakdown.phaseId}>
				<tr className="bg-gray-50/60 transition-colors hover:bg-gray-200/80">
					<td className="px-2 py-1 pl-8 text-xs font-semibold text-gray-800">
						{phase
							? <NavLink className="hover:text-primary-500 hover:underline"
									   to={getPhasePath(projectId, phase.phaseNumber)}>{phaseTitle}</NavLink>
							: phaseTitle}
					</td>
					<td className="px-4 py-1 text-right text-xs text-gray-800">
						{formatCentsToCurrency(phaseBreakdown.totalTrackedBillableCents)}
					</td>
					<td className="px-4 py-1 text-right text-xs text-gray-400">
						{formatCentsToCurrency(phaseBreakdown.totalTrackedNotBillableCents)}
					</td>
					<td />
				</tr>

				{phaseBreakdown.orderBreakdowns.map((orderBreakdown) => {
					const orderTitle = ordersById[orderBreakdown.orderId]?.title ?? "Unbekannter Auftrag";

					return <tr key={orderBreakdown.orderId}
							   className="bg-gray-50/60 transition-colors hover:bg-gray-200/80">
						<td className="px-2 py-1 pl-14 text-xs text-gray-600">
							{phase
								? <NavLink className="hover:text-primary-500 hover:underline"
										   to={getPhaseOrderPath(projectId, phase.phaseNumber, orderBreakdown.orderId)}>{orderTitle}</NavLink>
								: orderTitle}
						</td>
						<td className="px-4 py-1 text-right text-xs text-gray-700">
							{formatCentsToCurrency(orderBreakdown.totalTrackedBillableCents)}
						</td>
						<td className="px-4 py-1 text-right text-xs text-gray-400">
							{formatCentsToCurrency(orderBreakdown.totalTrackedNotBillableCents)}
						</td>
						<td />
					</tr>;
				})}
			</Fragment>;
		})}
	</tbody>;
};

export default ProjectRevenueMonthRow;
